import { FeatureFlag } from '@sparelabs/feature-flags'
import { IPolygon } from '@sparelabs/geography'
import { IListModelQueryParamValues, IModelJsonValues } from '@sparelabs/http-client-utils'
import { PermissionType } from '@sparelabs/permissions'
import { Currency } from './CurrencyTypes'
import { INotificationProvider } from './NotificationTypes'
import { ITimeRange, Language, UnitsOfDistance } from './SharedTypes'
import { TimeRulesBehaviour } from './TimeRuleTypes'

export enum ProductType {
  Launch = 'launch',
  Analyze = 'analyze',
  Analytics = 'analytics',
  Realize = 'realize',
  Admin = 'admin',
  // Tech Debt: "engage" is the historical name for this product. We're keeping it for now to avoid breaking changes
  Eligibility = 'engage',
  Resolve = 'resolve',
  Eam = 'eam',
  Ai = 'ai',
}

export enum OrganizationDriverLoginType {
  Email = 'email',
  Username = 'username',
  EmailAndUsername = 'emailAndUsername',
}

export interface ILaunchBrand {
  name: string
  photoUrl: string
  aboutUrl: string | null
}

/* ------------ USER AUTHENTICATION INTERFACES ---------------*/
export enum AuthProviderType {
  Phone = 'phone',
  Email = 'email',
}

export interface IAuthProvider {
  type: AuthProviderType
  metadata: unknown
}
/* ------------ USER AUTHENTICATION INTERFACES ---------------*/

export interface ITokenOrganizationIdPair {
  token: string
  organizationId: string
}

export type ITokenOrganizationIdPairs = ITokenOrganizationIdPair[]

export interface ILaunchService {
  enabled: boolean
  brand: ILaunchBrand
}

export interface ILaunchSettings {
  ondemand: ILaunchService
}

export interface ICallCenterConfig {
  ivrWelcomeMessage?: string
}

export interface IOrganizationPostBody {
  // These fields are required when creating an organization:
  name: string
  timezone: string

  // These fields are optional when creating an organization:
  defaultLocale?: string
  faqUrl?: string | null
  privacyPolicyUrl?: string | null
  featureFlags?: FeatureFlag[]
  isTestOnly?: boolean
  isVisibleToRiderApp?: boolean
  metadata?: Record<string, unknown>
  unitsOfDistance?: UnitsOfDistance
  /** Time of day offset when the service day starts, in seconds since midnight (0-86399) */
  startOfDayOffset?: number
  currency?: Currency
  isExternalNumericRiderIdEnabled?: boolean
  organizationKey?: string | null
  driversLoginField?: OrganizationDriverLoginType | null
  restrictedAreas?: IRestrictedArea[] | null
  enabledProducts?: ProductType[]
  automaticOverrideLimitSeconds?: number
  arrivedDistanceThresholdMeters?: number

  // These fields are actually not configurable in the super admin organization creation / edit portal,
  // and only configurable in the platform user organization settings page
  // TODO: remove and move default values to the backend?
  termsUrl?: string | null
  logoUrl?: string | null
  drivingSpeedMultiplier?: number
  blackoutWindow?: ITimeRange | null
  defaultLanguage?: Language
  additionalLanguages?: Language[]

  // These fields are used to set the agency data for GTFS export
  externalName?: string | null
  phoneNumber?: string | null
}

export interface IOrganizationPatchBody extends Partial<IOrganizationPostBody> {
  callCenterConfig?: ICallCenterConfig
  otpBasis?: OTPBasis
  notificationProviders?: INotificationProvider[] // This allows setting the fields 'twilioConfig' and 'messageBirdConfig' on Organization
  timeRulesBehaviour?: TimeRulesBehaviour
}

export interface IOrganizationResponse
  extends Omit<Required<IOrganizationPostBody>, 'enabledProducts'>,
    IModelJsonValues {
  /**
   *  @deprecated
   */
  paymentProviders: unknown[]
  /**
   *  @deprecated
   */
  payoutProviders: unknown[]
  /**
   *  @deprecated TODO: Remove this. Currently blocked by carpooling
   */
  products: ILaunchSettings
  /**
   *  @deprecated
   */
  launchSettings: ILaunchSettings
  isMaintenanceEnabled: boolean
  otpBasis: OTPBasis
  callCenterConfig: ICallCenterConfig
  restrictedAreas: IRestrictedArea[] | null
  incidentReportingUrl: string | null
  timeRulesBehaviour: TimeRulesBehaviour
  multimodalSettings: IMultimodalSettings | null
}

export enum OTPBasis {
  Pickup = 'pickup',
  Relevant = 'relevant',
}

export interface IOrganizationConfigurationPostBody {
  enableTripAnonymization: boolean
  enableUserAnonymization: boolean
  enableEventAuthorAnonymization: boolean
  enabledProducts: ProductType[]
  useOrganizationTerms: boolean
  isDemoEnabled: boolean
  weeklyReportsEnabled: boolean
  dailyReportsEnabled: boolean
  isMaintenanceEnabled: boolean
  silenceAlerts: boolean

  // The organization configuration operation only ever runs when creating and editing organizations in the super admin portal.
  // These settings are not configurable in this portal, only being configurable in the platform user organization settings page,
  // which calls the PATCH organization endpoint. So technically these fields are not doing anything.
  // TODO: remove
  blackoutWindow: ITimeRange | null
  defaultLanguage: Language
  otpBasis: OTPBasis
  additionalLanguages: Language[]
  callCenterConfig: ICallCenterConfig
  incidentReportingUrl: string | null
  multimodalSettings?: IMultimodalSettings | null
}

export interface IOrganizationConfigurationPatchBody extends Partial<IOrganizationConfigurationPostBody> {}

export interface IOrganizationConfigurationResponse extends IOrganizationConfigurationPostBody {
  superAdmin: boolean
  userAuthenticationProviders: IAuthProvider[]
  multimodalSettings: IMultimodalSettings | null
}

export interface IOrganizationListFilterParams {
  isTestOnly?: boolean
  isVisibleToRiderApp?: boolean
  isMember?: boolean
  isDriver?: boolean
  isAdmin?: boolean
  mobileAppId?: string
}

export interface IOrganizationListQueryParams extends IListModelQueryParamValues, IOrganizationListFilterParams {
  /**
   *  @deprecated
   */
  requiredPermission?: PermissionType.CAN_USE_ADMIN_PORTAL | PermissionType.CAN_USE_DRIVER_APP
}

export interface INearbyOrganizationListQueryParams
  extends Pick<IListModelQueryParamValues, 'limit' | 'skip'>,
    IOrganizationListFilterParams {
  longitude: number
  latitude: number
}

export interface INearbyOrganizationResponse {
  organization: IOrganizationResponse
  distance: number
}

export interface IGetPublicOrganizationQueryParams {
  organizationId: string
}

export interface IPublicOrganizationResponse {
  id: string
  name: string
  logoUrl: string | null
  isMaintenanceEnabled: boolean
  organizationKey: string | null
  enabledPublicFeatureFlags: FeatureFlag[]
}

// ------------------ RESTRICTED AREA ------------------
export interface IRestrictedArea {
  id: string
  area: IPolygon
  description: string | null
  startTs: number
  endTs?: number
}

export interface IRestrictedAreaPostBody extends IRestrictedArea {
  organizationId: string
}

export interface IMultimodalSettings {
  fixedRouteAntiCannibalization?: {
    numberOfTransfers?: number
    walkingDurationSeconds?: number
    totalJourneyDurationSeconds?: number
    departureDelaySeconds?: number
    earlyArrivalSeconds?: number
  }
}
